import { Fragment, type ReactNode } from 'react';

/**
 * Light markdown for notes: paragraphs, bullet lists, checklists that toggle in place,
 * headings, **bold**, *italic*, [links](https://...) and bare URLs.
 */

const INLINE =
  /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|\[[^\]]+\]\(https?:\/\/[^)\s]+\)|https?:\/\/[^\s)]+)/g;

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const tok = m[0];
    const key = `${keyBase}-${i++}`;
    if (tok.startsWith('**')) out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('*')) out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    else if (tok.startsWith('[')) {
      const mm = /^\[([^\]]+)\]\((.+)\)$/.exec(tok)!;
      out.push(
        <a key={key} href={mm[2]} target="_blank" rel="noreferrer noopener">
          {mm[1]}
        </a>,
      );
    } else {
      out.push(
        <a key={key} href={tok} target="_blank" rel="noreferrer noopener">
          {tok.replace(/^https?:\/\/(www\.)?/, '')}
        </a>,
      );
    }
    last = idx + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const CHECK = /^(\s*)[-*] \[( |x|X)\] (.*)$/;
const BULLET = /^(\s*)[-*] (.*)$/;
const HEADING = /^#{1,3} (.*)$/;

export interface MarkdownProps {
  text: string;
  /** Called with the new text when a checkbox is toggled. */
  onToggle?: (next: string) => void;
  className?: string;
  classes: Record<
    'checklist' | 'check' | 'done' | 'bullets' | 'heading' | 'para',
    string | undefined
  >;
}

export function Markdown({ text, onToggle, className, classes }: MarkdownProps) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  const toggle = (lineIndex: number) => {
    if (!onToggle) return;
    const next = [...lines];
    next[lineIndex] = next[lineIndex]!.replace(/\[( |x|X)\]/, (m) => (m === '[ ]' ? '[x]' : '[ ]'));
    onToggle(next.join('\n'));
  };
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    if (CHECK.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && CHECK.test(lines[i]!)) {
        const m = CHECK.exec(lines[i]!)!;
        const done = m[2] !== ' ';
        const idx = i;
        items.push(
          <li key={idx} className={`${classes.check} ${done ? classes.done : ''}`}>
            <input
              type="checkbox"
              checked={done}
              onChange={() => toggle(idx)}
              onClick={(e) => e.stopPropagation()}
              aria-label={m[3]}
            />
            <span>{inline(m[3]!, `c${idx}`)}</span>
          </li>,
        );
        i++;
      }
      blocks.push(
        <ul key={`cl${i}`} className={classes.checklist}>
          {items}
        </ul>,
      );
      continue;
    }
    if (BULLET.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && BULLET.test(lines[i]!) && !CHECK.test(lines[i]!)) {
        const m = BULLET.exec(lines[i]!)!;
        items.push(<li key={i}>{inline(m[2]!, `b${i}`)}</li>);
        i++;
      }
      blocks.push(
        <ul key={`ul${i}`} className={classes.bullets}>
          {items}
        </ul>,
      );
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      blocks.push(
        <p key={`h${i}`} className={classes.heading}>
          {inline(h[1]!, `h${i}`)}
        </p>,
      );
      i++;
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() &&
      !CHECK.test(lines[i]!) &&
      !BULLET.test(lines[i]!) &&
      !HEADING.test(lines[i]!)
    ) {
      para.push(lines[i]!);
      i++;
    }
    blocks.push(
      <p key={`p${i}`} className={classes.para}>
        {para.map((l, k) => (
          <Fragment key={k}>
            {k > 0 && <br />}
            {inline(l, `p${i}-${k}`)}
          </Fragment>
        ))}
      </p>,
    );
  }
  return <div className={className}>{blocks}</div>;
}

/** One line of light markdown (bold, italic, links, bare URLs), for short text. */
export function Inline({ text }: { text: string }) {
  return <>{inline(text, 'i')}</>;
}
